/**
 * The three tests of the verifier, which is the only question this repository can honestly ask
 * of itself: are these predicates measuring anything?
 *
 * One — break the book on purpose in a named way, and the rules aimed at that break must fault
 * more. Two — run the rules forwards from the bare harmony and see how far from Rimsky-Korsakov
 * they land; that distance is the number a good verdict cannot fake. Three — ask them about the
 * nineteen figures the book captions good or bad itself.
 */

import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import Board from "../components/Board";
import { ACTION } from "../components/Chrome";
import { Failed, Kicker, Loading, Note } from "../components/Page";
import { count, semitones } from "../lib/format";

function Figure({
  value,
  of,
  caption,
}: {
  value: string;
  of?: string;
  caption: string;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-rule bg-leaf p-4">
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono text-[26px] leading-none">{value}</span>
        {of ? <span className="font-mono text-xs text-ink-quiet">{of}</span> : null}
      </div>
      <p className="mt-1.5 text-[11px] leading-snug text-ink-faded">{caption}</p>
    </div>
  );
}

export default function EvidencePage(): JSX.Element {
  const { data, error, loading } = useAsync("evidence", () => api.evidence());

  const shell = (children: JSX.Element): JSX.Element => (
    <Board kicker="Is the verifier real?" title="Evidence" measure="wide">
      {children}
    </Board>
  );
  if (loading) return shell(<Loading what="the evidence" />);
  if (error !== null) return shell(<Failed error={error} />);
  if (data === null) return shell(<Failed error="the evidence came back empty" />);

  const { degrade, compare, figures } = data;

  return (
    <Board
      kicker="Is the verifier real?"
      title="Evidence"
      lede="Three tests. None of them is the score the rules give the book — that number cannot tell you whether the rules are worth anything."
      measure="wide"
      actions={
        <Link to="/evidence/figures" className={ACTION}>
          Labelled figures
        </Link>
      }
    >
      <div className="space-y-8">
        {data.note ? <Note>{data.note}</Note> : null}

        <section className="space-y-3">
          <Kicker>1 · Break the book on purpose</Kicker>
          {degrade === null ? (
            <p className="text-sm text-ink-faded">Not measured yet.</p>
          ) : (
            <>
              <div className="overflow-hidden rounded-xl border border-rule bg-leaf">
                <div className="flex items-baseline gap-3 border-b border-rule px-4 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-quiet">
                  <span className="w-24 shrink-0">mutation</span>
                  <span className="flex-1">aimed at</span>
                  <span className="w-16 shrink-0 text-right">landed</span>
                  <span className="w-16 shrink-0 text-right">rise</span>
                </div>
                {degrade.rows.map((row) => {
                  const rise = row.targets
                    .map((name) => {
                      const before = row.before[name];
                      const after = row.after[name];
                      return before === undefined ||
                        before === null ||
                        after === undefined ||
                        after === null
                        ? null
                        : (before - after) * 100;
                    })
                    .filter((one): one is number => one !== null);
                  const most = rise.length > 0 ? Math.max(...rise) : null;
                  return (
                    <div
                      key={row.name}
                      className="flex items-baseline gap-3 border-b border-rule-faint px-4 py-3 text-xs last:border-b-0"
                    >
                      <span className="w-24 shrink-0 font-mono">{row.name}</span>
                      <span className="flex-1 text-ink-faded">
                        {row.targets.length === 0
                          ? "nothing — the control"
                          : row.targets.join(", ")}
                      </span>
                      <span className="w-16 shrink-0 text-right font-mono text-ink-quiet">
                        {count(row.changed)}
                      </span>
                      <span
                        className={`w-16 shrink-0 text-right font-mono ${
                          most !== null && most > 0.05 ? "text-fault" : "text-ink-quiet"
                        }`}
                      >
                        {most === null ? "—" : `${most > 0 ? "+" : ""}${most.toFixed(1)} pp`}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="max-w-2xl text-xs leading-relaxed text-ink-faded">
                Each mutation is aimed at named rules, and those are the fault rates that have to
                rise. <span className="font-mono">identity</span> edits nothing and must not move —
                it is the control. A mutation that lands on only a tenth of the book makes a small
                rise a thin sample, not a weak rule.
              </p>
            </>
          )}
        </section>

        <section className="space-y-3">
          <Kicker>2 · Distance from Rimsky-Korsakov</Kicker>
          {compare === null ? (
            <p className="text-sm text-ink-faded">Not measured yet.</p>
          ) : (
            <>
              <p className="text-sm leading-relaxed text-ink-faded">
                <span className="font-mono text-ink">{semitones(compare.drift)}</span> semitones of
                mean drift when the generator re-voices the book&apos;s own{" "}
                {count(compare.compared)} chords from the written bass.
              </p>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Figure
                  value={count(compare.identical)}
                  of={`of ${count(compare.compared)}`}
                  caption="chords come back note for note"
                />
                <Figure
                  value={count(compare.samePitches)}
                  of={`of ${count(compare.compared)}`}
                  caption="sound the same pitch set, whoever plays them"
                />
                <Figure
                  value={count(compare.examples)}
                  caption="examples the generator can be handed as a brief"
                />
                <Figure
                  value={semitones(compare.worst?.drift)}
                  of={compare.worst ? `No. ${compare.worst.example}` : undefined}
                  caption="the furthest it ever lands from him"
                />
              </div>
              {compare.worst ? (
                <Link
                  to={`/book/${compare.worst.example}/compare`}
                  className="inline-flex min-h-[44px] items-center text-sm font-medium text-clean hover:text-clean-strong"
                >
                  Look at the worst one →
                </Link>
              ) : null}
            </>
          )}
        </section>

        <section className="space-y-3">
          <Kicker>3 · The book&apos;s own labels</Kicker>
          {figures === null ? (
            <p className="text-sm text-ink-faded">Not measured yet.</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Figure
                  value={`${figures.goodFaulted}`}
                  of={`/ ${figures.goodTotal}`}
                  caption="printed as models, faulted anyway"
                />
                <Figure
                  value={`${figures.badFaulted}`}
                  of={`/ ${figures.badTotal}`}
                  caption="warned against, and caught"
                />
                <Figure value={`${figures.unread}`} caption="MP3 only — unread, not passed" />
              </div>
              <Link
                to="/evidence/figures"
                className="inline-flex min-h-[44px] items-center text-sm font-medium text-clean hover:text-clean-strong"
              >
                All {figures.labelled} labelled figures →
              </Link>
            </>
          )}
        </section>

        <p className="max-w-2xl rounded-xl border border-rule bg-leaf p-4 text-xs leading-relaxed text-ink-faded">
          A good verdict on what comes out proves nothing — the search optimises exactly what the
          verifier measures. The drift, and whether a deliberate break moves the rule aimed at it,
          are the numbers that mean something.
        </p>

        {compare ? (
          <p className="font-mono text-[11px] text-ink-quiet">measured {compare.builtAt}</p>
        ) : null}
      </div>
    </Board>
  );
}
